import { Component, Inject, Input, OnInit } from '@angular/core';
import {
  MasterPayrollObject,
  PayDateInfoEx,
  PayrollStartConfig,
  PayrollStartData,
  PayrollStatuses,
  PayTypes,
  Schedule
} from '../../../../models/payroll';
import { Router } from '@angular/router';
import { PayrollService } from '../../../../services/payroll.service';
import { InstanceTypes } from '../../../../models/instance-types';
import { IsPayDateInvalid } from '../PaydateValidationFunction';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { ClientSetup } from '../../../../models/client';
import { BaseClientComponent } from '../../../../components/base/base-client.component';
import { ModalService } from '../../../../services/modal.service';
import { ModalStates } from '../../../../models/modal';
import { first, tap } from 'rxjs/operators';
import { AuthService } from '../../../../services/auth.service';
import { AuditService } from '../../../../services/audit.service';
import { PayrollFrequency } from '../../../../models/payroll-info';
import { AutoPayrollSkipModal } from '../../auto-payroll-skip-modal/auto-payroll-skip-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';

@Component({
  selector: 'lib-run-auto-payroll-tile',
  templateUrl: './run-auto-payroll-tile.component.html',
  styleUrls: ['./run-auto-payroll-tile.component.css'],
  standalone: false
})
export class RunAutoPayrollTileComponent extends BaseClientComponent implements OnInit {
  @Input() clientCode: string;
  @Input() clientSetup: ClientSetup;
  @Input() payrollStartData: PayrollStartData;
  @Input() activePayroll: MasterPayrollObject;

  public payDate: string;
  public periodEnd: string;
  public pdi: PayDateInfoEx;
  public dateUntilNextPayrollSubmission: string;
  public daysUntilNextPayrollSubmission: number;
  public payrollStartConfig: PayrollStartConfig;

  get isPayDateInvalid() {
    return this.activePayroll && IsPayDateInvalid(this.activePayroll);
  }

  get isClientPortal() {
    return this.env.instanceType == InstanceTypes.ClientPortal;
  }

  get isSubmitDisabled() {
    return this.payrollStartConfig.IsLocked || this.activePayroll?.IsAutomaticSkipped;
  }

  get payTypes() {
    return PayTypes;
  }

  constructor(
    private authService: AuthService,
    private auditService: AuditService,
    private router: Router,
    private payrollService: PayrollService,
    private payrollMgmt: PayrollManagement,
    private modalService: ModalService,
    private ngModalService: NgbModal,
    @Inject('env') private env: any
  ) {
    super();
  }

  ngOnInit(): void {
    this._setDateInfoLeftForPayroll();
    this.payrollStartConfig = this.payrollMgmt.buildPayrollConfig(
      this.clientSetup,
      this.activePayroll,
      null,
      PayTypes.Regular
    );
  }

  //#region Public Methods

  public navigatePayroll() {
    this.router.navigate([`/client/${this.clientCode}/autopayroll/`]);
  }

  skipAutomaticPay() {
    var modalRef = this.ngModalService.open(AutoPayrollSkipModal, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'centered'
    });

    const c = modalRef.componentInstance as AutoPayrollSkipModal;

    c.payDate = this.pdi.TargetPayDate;
    c.periodEndDate = this.pdi.PeriodEnd;

    c.onSkip = () => {
      this.confirmSkipAutoPay().subscribe(() => {
        modalRef.close();
        // We need to wait for the audit event to be recorded before we reload the page
        this.auditPayrollSkippedWithResponse().subscribe(() => {
          window.location.reload();
        });
      });
    };

    c.onSkipAndNavigate = () => {
      this.confirmSkipAutoPay().subscribe(() => {
        modalRef.close();
        this.auditPayrollSkipped();
        this.router.navigate([`/client/${this.clientCode}/payroll`]);
      });
    };

    c.onCancel = () => {
      modalRef.close();
    };
  }

  private confirmSkipAutoPay(): Observable<any> {
    if (this.activePayroll) {
      this.activePayroll.IsAutomaticSkipped = true;
      this.activePayroll.LastEditOn = new Date();
      this.activePayroll.LastEditBy = this.authService.getCurrentUserName();
      return this.payrollService.updatePayGrid(
        this.clientCode,
        this.activePayroll.Id,
        this.activePayroll
      );
    } else {
      // create new paygrid record and set it as skipped
      const location = this.payrollStartData.Locations[0];
      const cal = location.Calendars[0];
      const payGrid = new MasterPayrollObject(
        this.clientCode,
        [
          new Schedule(
            cal.CalendarId,
            0,
            this.pdi.PeriodEnd,
            this.pdi.Frequency,
            cal.CalendarName,
            this.pdi.PeriodBegin,
            this.pdi.PeriodEnd,
            this.pdi.TargetPayDate
          )
        ],
        location.Id,
        this.pdi.TargetPayDate,
        true,
        false,
        PayrollStatuses.Started,
        this.authService.getCurrentUserName(),
        new Date()
      );
      return this.payrollService.createPayGrid(this.clientCode, payGrid).pipe(
        tap(id => {
          payGrid.Id = id;
          this.activePayroll = payGrid;
        })
      );
    }
  }

  private auditPayrollSkipped() {
    this.auditService.auditPayroll(`auto-payroll-skipped`, {
      ClientCode: this.clientCode,
      PayrollId: this.activePayroll.Id,
      PayDate: this.activePayroll.PayDate,
      LocationId: this.activePayroll.LocationId,
      Calendars: this.activePayroll.Calendars,
      PayrollType: this.activePayroll.PayType
    });
  }

  private auditPayrollSkippedWithResponse() {
    return this.auditService.auditPayrollWithResponse(`auto-payroll-skipped`, {
      ClientCode: this.clientCode,
      PayrollId: this.activePayroll.Id,
      PayDate: this.activePayroll.PayDate,
      LocationId: this.activePayroll.LocationId,
      Calendars: this.activePayroll.Calendars,
      PayrollType: this.activePayroll.PayType
    });
  }
  //#endregion

  //#region Private Methods

  private _setDateInfoLeftForPayroll(): void {
    // FIXME: will need addressed when multiple locations are enabled
    let earliestPayDate: PayDateInfoEx;

    if (this.activePayroll) {
      var location = this.payrollStartData.Locations.find(
        x => x.Id == this.activePayroll.LocationId
      );

      earliestPayDate = location.NextPayDates.find(
        x => x.CalendarId == location.Calendars[0].CalendarId
      );
    } else {
      earliestPayDate = this.payrollStartData.Locations[0].NextPayDates?.reduce((a, b) => {
        return new Date(a.ProcessDueDate) < new Date(b.ProcessDueDate) ? a : b;
      });
    }

    // get days left
    const difference = +new Date(earliestPayDate.ProcessDueDate) - +new Date();

    // if the due date is today or passed, then display 0
    this.daysUntilNextPayrollSubmission =
      difference >= 0 ? Math.floor(difference / (1000 * 3600 * 24)) : 0;
    this.dateUntilNextPayrollSubmission = new Date(
      earliestPayDate.ProcessDueDate
    ).toLocaleDateString('en-us', { day: 'numeric', month: 'long' });

    this.payDate = new Date(earliestPayDate.TargetPayDate).toLocaleDateString('en-us', {
      day: 'numeric',
      month: 'long'
    });

    this.periodEnd = new Date(earliestPayDate.PeriodEnd).toLocaleDateString('en-us', {
      day: 'numeric',
      month: 'long'
    });

    this.pdi = earliestPayDate;
  }

  //#endregion
}
